import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import parse from 'html-react-parser';
import {contentMaxWidth, borderRadius, colors, fontWeight} from '@/styles/tokens';

interface ComparisonRow {
    parameter: string;
    tradfi: {badge: string; text: string};
    defi: {badge: string; text: string};
}

interface DeFiVsTradFiProps {
    data: {
        heading: string;
        subheading: string;
        columns: {label: string; id: string}[];
        rows: ComparisonRow[];
    };
}

const DeFiVsTradFi = ({data}: DeFiVsTradFiProps) => {
    const {heading, subheading, columns, rows} = data;

    return (
        <Box>
            <MUIContainer>
                <Box sx={{textAlign: 'center', mb: 6, maxWidth: contentMaxWidth.standard, mx: 'auto'}}>
                    <Typography
                        variant="h2"
                        component="h2"
                        className="font-headers"
                        sx={{
                            fontSize: {xs: '2rem', md: '3rem'},
                            fontWeight: fontWeight.extrabold,
                            mb: 3,
                            lineHeight: 1.2,
                            color: '#000'
                        }}
                    >
                        {heading}
                    </Typography>
                    <Typography
                        variant="body1"
                        className="font-regular-text remove-underline"
                        sx={{
                            fontSize: '16px',
                            color: colors.text.primary,
                            lineHeight: 1.8
                        }}
                    >
                        {parse(subheading)}
                    </Typography>
                </Box>

                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        borderRadius: '0px',
                        border: '1px solid #afafaf',
                        overflowX: 'auto',
                        boxShadow: 'none',
                        '&::-webkit-scrollbar': {height: '6px'},
                        '&::-webkit-scrollbar-thumb': {backgroundColor: '#afafaf', borderRadius: borderRadius['10']}
                    }}
                >
                    <Table sx={{minWidth: 950}}>
                        <TableHead>
                            <TableRow>
                                {columns?.map((col, index) => (
                                    <TableCell
                                        key={col.id}
                                        className="font-headers"
                                        sx={{
                                            bgcolor: index === 0 ? '#fff' : index === 1 ? '#F7F9FC' : '#FFF4EB',
                                            color: index === 1 ? '#244486' : index === 2 ? '#f7933c' : '#000',
                                            fontWeight: fontWeight.extrabold,
                                            fontSize: '1.2rem',
                                            py: 4,
                                            px: 4,
                                            borderBottom: '1px solid #afafaf',
                                            borderLeft: index === 1 ? '2px solid #244486' : index === 2 ? '2px solid #f7933c' : 'none',
                                            width: index === 0 ? '18%' : '41%',
                                            textAlign: 'left',
                                            verticalAlign: 'middle'
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows?.map((row, index) => {
                                const isLastRow = index === rows.length - 1;
                                return (
                                    <TableRow key={index} sx={{bgcolor: '#fff'}}>
                                        <TableCell
                                            className="font-headers"
                                            sx={{
                                                fontWeight: fontWeight.bold,
                                                color: '#111827',
                                                px: 4,
                                                py: 4,
                                                bgcolor: '#fff',
                                                borderRight: 'none',
                                                borderBottom: isLastRow ? 'none' : '1px solid #afafaf',
                                                fontSize: '18px',
                                                verticalAlign: 'middle'
                                            }}
                                        >
                                            {row.parameter}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                px: 4,
                                                py: 4,
                                                bgcolor: colors.background.subtle,
                                                borderLeft: '2px solid #244486',
                                                borderBottom: isLastRow ? 'none' : '1px solid #afafaf',
                                                verticalAlign: 'top'
                                            }}
                                        >
                                            <Box
                                                className="font-regular-text"
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: borderRadius['2xl'],
                                                    bgcolor: '#DBEAFE',
                                                    color: '#000',
                                                    fontSize: '12px',
                                                    fontWeight: fontWeight.semibold,
                                                    mb: 1.5,
                                                    textTransform: 'capitalize',
                                                    letterSpacing: '0.03rem'
                                                }}
                                            >
                                                {row.tradfi.badge}
                                            </Box>
                                            <Typography
                                                className="font-regular-text"
                                                sx={{fontSize: '16px', color: colors.text.secondary, lineHeight: 1.6}}
                                            >
                                                {row.tradfi.text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                px: 4,
                                                py: 4,
                                                bgcolor: '#FFF4EB',
                                                borderLeft: '2px solid #f7933c',
                                                borderBottom: isLastRow ? 'none' : '1px solid #afafaf',
                                                verticalAlign: 'top'
                                            }}
                                        >
                                            <Box
                                                className="font-regular-text"
                                                sx={{
                                                    display: 'inline-block',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: borderRadius['2xl'],
                                                    bgcolor: '#f9dfbc',
                                                    color: '#000',
                                                    fontSize: '0.75rem',
                                                    fontWeight: fontWeight.semibold,
                                                    mb: 1.5,
                                                    textTransform: 'capitalize',
                                                    letterSpacing: '0.03rem'
                                                }}
                                            >
                                                {row.defi.badge}
                                            </Box>
                                            <Typography
                                                className="font-regular-text"
                                                sx={{fontSize: '1.05rem', color: colors.text.secondary, lineHeight: 1.6}}
                                            >
                                                {row.defi.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </MUIContainer>
        </Box>
    );
};

export default memo(DeFiVsTradFi);
